/**
 * System Configuration Service
 * Handles fetching and caching of system configuration values
 */

import ApiService from './api'
import AppURLs from '../config/urls'

class SystemConfigService {
  constructor() {
    this.configCache = new Map()
    this.cacheTimestamp = null
    this.cacheExpiry = 5 * 60 * 1000 // 5 minutes cache
  }

  /**
   * Get all system configurations
   * @returns {Promise<Object>} - API response with configurations
   */
  async getAllConfigs() {
    try {
      console.log('📋 Fetching system configurations...')
      
      const response = await ApiService.get(AppURLs.SYSTEM_CONFIGS.GET_ALL)
      
      if (response.success && response.data) {
        // Cache the configurations
        this.cacheConfigs(response.data)
        console.log('✅ System configurations fetched successfully')
        return response
      } else {
        throw new Error(response.message || 'Failed to fetch configurations')
      }
    } catch (error) {
      console.error('❌ Error fetching system configurations:', error)
      throw error
    }
  }

  /**
   * Cache configurations in memory
   * @param {Array} configs - Array of configuration objects
   */
  cacheConfigs(configs) {
    this.configCache.clear()
    this.cacheTimestamp = Date.now()
    
    configs.forEach(config => {
      this.configCache.set(config.key, config.value)
    })
    
    console.log('💾 Cached configurations:', Array.from(this.configCache.keys()))
  }

  /**
   * Check if cache is valid
   * @returns {boolean} - True if cache is valid
   */
  isCacheValid() {
    return this.cacheTimestamp && 
           (Date.now() - this.cacheTimestamp) < this.cacheExpiry &&
           this.configCache.size > 0
  }

  /**
   * Get a specific configuration value by key
   * @param {string} key - Configuration key
   * @param {any} defaultValue - Default value if not found
   * @returns {Promise<any>} - Configuration value
   */
  async getConfigValue(key, defaultValue = null) {
    try {
      // Check cache first
      if (this.isCacheValid() && this.configCache.has(key)) {
        console.log(`📋 Retrieved config from cache: ${key} = ${this.configCache.get(key)}`)
        return this.configCache.get(key)
      }

      // Fetch fresh data if cache is invalid or key not found
      await this.getAllConfigs()
      
      const value = this.configCache.get(key)
      if (value !== undefined) {
        console.log(`📋 Retrieved config: ${key} = ${value}`)
        return value
      } else {
        console.warn(`⚠️ Configuration not found: ${key}, using default: ${defaultValue}`)
        return defaultValue
      }
    } catch (error) {
      console.error(`❌ Error getting config value for ${key}:`, error)
      return defaultValue
    }
  }

  /**
   * Get service fee rate configuration
   * @returns {Promise<number>} - Service fee rate as percentage (0-100)
   */
  async getServiceFeeRate() {
    return await this.getConfigValue('ORDER_SERVICE_FEE_RATE', 10)
  }

  /**
   * Get package fee rate configuration
   * @returns {Promise<number>} - Package fee rate as percentage (0-100)
   */
  async getPackageFeeRate() {
    return await this.getConfigValue('PACKAGE_FEE_RATE', 5)
  }

  /**
   * Get tax rate configuration
   * @returns {Promise<number>} - Tax rate as percentage
   */
  async getTaxRate() {
    return await this.getConfigValue('TAX_RATE', 8.5)
  }

  /**
   * Get default currency configuration
   * @returns {Promise<string>} - Default currency symbol
   */
  async getDefaultCurrency() {
    return await this.getConfigValue('DEFAULT_CURRENCY', 'Rs.')
  }

  /**
   * Get loyalty point rate configuration
   * @returns {Promise<number>} - Loyalty points per currency unit
   */
  async getLoyaltyPointRate() {
    return await this.getConfigValue('LOYALTY_POINT_RATE', 3)
  }

  /**
   * Update a system configuration by KEY
   * @param {string} configId - Configuration ID  
   * @param {Object} data - Update data (e.g., { value: newValue })
   * @param {string} configKey - Configuration KEY
   * @returns {Promise<Object>} - API response
   */
  async updateConfig(configId, data, configKey) {
    try {
      console.log('📝 Updating system config by KEY:', configKey, data)
      
      // Use PATCH with the key in the URL path
      const url = `/system-configs/${configKey}`
      console.log('📝 Update URL:', url)
      const response = await ApiService.patch(url, data)
      
      if (response.success) {
        this.clearCache()
        console.log('✅ System config updated successfully')
        return response
      } else {
        throw new Error(response.message || 'Failed to update configuration')
      }
    } catch (error) {
      console.error('❌ Error updating system config:', error)
      throw error
    }
  }

  /**
   * Clear configuration cache
   */
  clearCache() {
    this.configCache.clear()
    this.cacheTimestamp = null
    console.log('🗑️ Configuration cache cleared')
  }

  /**
   * Refresh configurations (clear cache and fetch fresh data)
   * @returns {Promise<Object>} - Fresh configuration data
   */
  async refreshConfigs() {
    this.clearCache()
    return await this.getAllConfigs()
  }
}

// Export singleton instance
const systemConfigService = new SystemConfigService()
export default systemConfigService
